/**
 * Explicit bridge between the two halves of the site.
 *
 * The video-game database (Pal stats, breeding, capture rates, mounts) and the
 * physical trading-card side (card list, prices, price index) cover different
 * subjects, so links between them always spell out which world they lead to.
 * No bare "cards" or "see more" anchors that leave a crawler guessing whether a
 * page is about the game or the TCG.
 */

interface Bridge {
  href: string;
  label: string;
  blurb: string;
}

const TO_TCG: Bridge[] = [
  { href: "/cards", label: "Palworld TCG card list (physical trading cards)", blurb: "Every printed English card number, set and rarity — the tabletop game, not the video game roster." },
  { href: "/prices", label: "Palworld TCG card prices", blurb: "Market prices for printed singles, tracked daily." },
  { href: "/tcg-price-index", label: "Palworld TCG Price Index", blurb: "Whether printed card values are rising or falling." },
];

const TO_GAME: Bridge[] = [
  { href: "/pals", label: "Palworld Pal database (video game stats)", blurb: "In-game stats, drops, work suitability and spawn locations — not card values." },
  { href: "/breeding", label: "Palworld breeding calculator (video game)", blurb: "Parent pairs and the exact egg they hatch in-game." },
  { href: "/capture-rates", label: "Palworld capture rate table (video game)", blurb: "Catch chance and sphere tier for every Pal." },
];

export function CrossCategoryLinks({
  side,
  className = "mt-10",
}: {
  /** Which half the current page belongs to; links point to the other half. */
  side: "game" | "tcg";
  className?: string;
}) {
  const isGame = side === "game";
  const links = isGame ? TO_TCG : TO_GAME;
  const heading = isGame
    ? "Looking for the Palworld trading card game instead?"
    : "Looking for Palworld video game data instead?";
  const intro = isGame
    ? "This page covers the video game. The printed Palworld Trading Card Game is tracked separately:"
    : "This page covers the printed trading card game. In-game Pal data lives on separate pages:";

  return (
    <nav aria-label={heading} className={`rounded-2xl border bg-card/40 p-6 ${className}`}>
      <h2 className="text-lg font-semibold">{heading}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{intro}</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3">
        {links.map((l) => (
          <li key={l.href} className="rounded-lg border border-border/60 bg-background/40 p-3">
            <a href={l.href} className="text-sm font-semibold text-primary hover:underline">
              {l.label}
            </a>
            <p className="mt-1 text-xs text-muted-foreground">{l.blurb}</p>
          </li>
        ))}
      </ul>
    </nav>
  );
}
